import { FC, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { PickerListProps } from './_models';

const PickerList: FC<PickerListProps> = ({ items, value, onChange }) => {
  const containerHeight = 210;
  const itemHeight = 32;
  const replicationFactor = 4;
  const centerCopy = Math.ceil(replicationFactor / 2);
  const baseIndex = centerCopy * items.length;

  const replicatedItems = useMemo(
    () =>
      Array.from({ length: replicationFactor })
        .map(() => items)
        .flat(),
    [items]
  );

  const containerRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const scrollTimeout = useRef<number | null>(null);
  const currentIndex = useRef<number>(0);

  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop =
        (baseIndex + currentIndex.current + 1) * itemHeight;
    }
  }, [baseIndex, itemHeight]);

  // Scroll event handler with debounced recentering.
  const handleScroll = (event: Event) => {
    console.debug('handleScroll');
    const target = event.target as HTMLUListElement;
    const scrollTop = Math.max(target.scrollTop, 0);
    const virtualIndex = Math.round(scrollTop / itemHeight - 1);

    // Clear any existing recentering timeout.
    if (scrollTimeout.current) {
      window.clearTimeout(scrollTimeout.current);
    }

    // Set a debounce timeout to recenter and update the effective value
    scrollTimeout.current = window.setTimeout(() => {
      const effectiveIndex = virtualIndex % items.length;
      if (virtualIndex < centerCopy * items.length) {
        target.scrollTop = (baseIndex + effectiveIndex) * itemHeight;
      } else if (virtualIndex >= (centerCopy + 1) * items.length) {
        target.scrollTop =
          (baseIndex + effectiveIndex + centerCopy) * itemHeight;
      }

      currentIndex.current = effectiveIndex;
      if (!items[effectiveIndex]) return;
      onChange(items[effectiveIndex].value);
    }, 50);
  };

  useEffect(() => {
    containerRef.current?.addEventListener('scroll', handleScroll);
    return () => {
      containerRef.current?.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) window.clearTimeout(scrollTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, onChange]);

  // When external value changes, update scroll position.
  useEffect(() => {
    const newIndex = items.findIndex((item) => item.value === value);
    if (newIndex !== -1 && newIndex !== currentIndex.current) {
      currentIndex.current = newIndex;
      containerRef.current?.scrollTo({
        top: (baseIndex + newIndex + 1) * itemHeight,
        behavior: 'smooth',
      });
    }
  }, [value, items, baseIndex, itemHeight]);

  return (
    <ul
      className="picker-list"
      ref={containerRef}
      style={{ height: `${containerHeight}px` }}
    >
      {replicatedItems.map((item, index) => (
        <li
          key={`${item.value}-${index}`}
          ref={(node) => (itemRefs.current[index] = node)}
          className="picker-item"
          style={{ height: `${itemHeight}px`, lineHeight: `${itemHeight}px` }}
        >
          <div>{item.label}</div>
        </li>
      ))}
    </ul>
  );
};

export default PickerList;
